import { fetchDataApi } from '../../../shared/api/fetchDataApi';
import type {
  AddBoardColumnPayload,
  BoardCardAssignment,
  BoardCardAssignmentInput,
  BoardCardPriority,
  BoardDetail,
  BoardViewCard,
  BoardViewResponse,
  CreateBoardCardPayload,
  CreateBoardPayload,
  EligibleDatasetsResponse,
  MoveBoardCardPayload,
  MoveBoardCardResult,
  ProfileBoardsResponse,
  RenameBoardColumnPayload,
  UpdateBoardCardOrderPayload,
  UpdateBoardCardPayload,
  UpdateBoardColumnsOrderPayload,
  UpdateBoardPayload,
  WorkspaceBoardsResponse,
} from '../types/boardTypes';

export type {
  BoardSummary,
  BoardDetail,
  BoardProfileSection,
  ProfileBoardsResponse,
  WorkspaceBoardsResponse,
  EligibleDatasetSummary,
  EligibleDatasetsResponse,
  BoardViewColumn,
  BoardViewCard,
  BoardViewResponse,
  BoardCardAssignment,
  BoardCardAssignmentInput,
  BoardCardPriority,
  CreateBoardPayload,
  UpdateBoardPayload,
  CreateBoardCardPayload,
  UpdateBoardCardPayload,
  MoveBoardCardPayload,
  MoveBoardCardResult,
  AddBoardColumnPayload,
  RenameBoardColumnPayload,
  UpdateBoardColumnsOrderPayload,
  UpdateBoardCardOrderPayload,
  BoardSseEvent,
  BoardSseEventType,
} from '../types/boardTypes';

type ApiSuccess<T> = T & { success: boolean };

export async function apiListBoardsForProfile(): Promise<ProfileBoardsResponse> {
  const data = await fetchDataApi<ApiSuccess<ProfileBoardsResponse>>('/boards/profile', {
    method: 'GET',
  });

  return {
    own: data.own ?? [],
    shared: data.shared ?? [],
  };
}

export async function apiListBoardsByRoot(
  rootFolderId: string,
): Promise<WorkspaceBoardsResponse> {
  const data = await fetchDataApi<ApiSuccess<WorkspaceBoardsResponse>>(
    `/boards?rootFolderId=${encodeURIComponent(rootFolderId)}`,
    { method: 'GET' },
  );

  return {
    rootFolderId: data.rootFolderId ?? rootFolderId,
    accessRole: data.accessRole,
    canMoveCards: data.canMoveCards,
    canEditCards: data.canEditCards,
    canManageBoard: data.canManageBoard,
    boards: data.boards ?? [],
  };
}

export async function apiListEligibleDatasetsForBoards(
  rootFolderId: string,
): Promise<EligibleDatasetsResponse> {
  const data = await fetchDataApi<ApiSuccess<EligibleDatasetsResponse>>(
    `/boards/eligible-datasets?rootFolderId=${encodeURIComponent(rootFolderId)}`,
    { method: 'GET' },
  );

  return {
    rootFolderId: data.rootFolderId ?? rootFolderId,
    accessRole: data.accessRole,
    canMoveCards: data.canMoveCards,
    canEditCards: data.canEditCards,
    canManageBoard: data.canManageBoard,
    datasets: data.datasets ?? [],
  };
}

export async function apiGetBoard(boardId: string): Promise<BoardDetail> {
  const data = await fetchDataApi<ApiSuccess<{ board: BoardDetail }>>(
    `/boards/${encodeURIComponent(boardId)}`,
    { method: 'GET' },
  );
  return data.board;
}

export async function apiGetBoardView(boardId: string): Promise<BoardViewResponse> {
  const data = await fetchDataApi<ApiSuccess<BoardViewResponse>>(
    `/boards/${encodeURIComponent(boardId)}/view`,
    { method: 'GET' },
  );

  return {
    board: data.board,
    columns: data.columns ?? [],
    cards: data.cards ?? {},
    truncated: Boolean(data.truncated),
    loadedRowCount: data.loadedRowCount ?? 0,
    totalRowCount: data.totalRowCount ?? 0,
    assignmentsByRowId: data.assignmentsByRowId ?? {},
    priorityByRowId: data.priorityByRowId ?? {},
  };
}

export async function apiGetBoardCardAssignments(
  boardId: string,
  rowId: string,
): Promise<BoardCardAssignment[]> {
  const data = await fetchDataApi<ApiSuccess<{ assignments: BoardCardAssignment[] }>>(
    `/boards/${encodeURIComponent(boardId)}/cards/${encodeURIComponent(rowId)}/assignments`,
    { method: 'GET' },
  );
  return data.assignments ?? [];
}

export async function apiReplaceBoardCardAssignments(
  boardId: string,
  rowId: string,
  assignments: BoardCardAssignmentInput[],
): Promise<BoardCardAssignment[]> {
  const data = await fetchDataApi<ApiSuccess<{ assignments: BoardCardAssignment[] }>>(
    `/boards/${encodeURIComponent(boardId)}/cards/${encodeURIComponent(rowId)}/assignments`,
    {
      method: 'PUT',
      data: { assignments },
    },
  );
  return data.assignments ?? [];
}

export async function apiUpdateBoardCardMeta(
  boardId: string,
  rowId: string,
  priority: BoardCardPriority | null,
): Promise<{ boardId: string; rowId: string; priority: BoardCardPriority | null }> {
  const data = await fetchDataApi<ApiSuccess<{
    meta: {
      boardId: string;
      rowId: string;
      priority: BoardCardPriority | null;
    };
  }>>(
    `/boards/${encodeURIComponent(boardId)}/cards/${encodeURIComponent(rowId)}/meta`,
    {
      method: 'PATCH',
      data: { priority },
    },
  );
  return {
    boardId: data.meta.boardId,
    rowId: data.meta.rowId,
    priority: data.meta.priority ?? null,
  };
}

export async function apiCreateBoard(payload: CreateBoardPayload): Promise<BoardDetail> {
  const data = await fetchDataApi<ApiSuccess<{ board: BoardDetail }>>('/boards', {
    method: 'POST',
    data: payload,
  });
  return data.board;
}

export async function apiUpdateBoard(
  boardId: string,
  payload: UpdateBoardPayload,
): Promise<BoardDetail> {
  const data = await fetchDataApi<ApiSuccess<{ board: BoardDetail }>>(
    `/boards/${encodeURIComponent(boardId)}`,
    {
      method: 'PATCH',
      data: payload,
    },
  );
  return data.board;
}

export async function apiDeleteBoard(boardId: string): Promise<void> {
  await fetchDataApi(`/boards/${encodeURIComponent(boardId)}`, {
    method: 'DELETE',
  });
}

export async function apiCreateBoardCard(
  boardId: string,
  payload: CreateBoardCardPayload,
): Promise<BoardViewCard> {
  const data = await fetchDataApi<ApiSuccess<{ card: BoardViewCard }>>(
    `/boards/${encodeURIComponent(boardId)}/cards`,
    {
      method: 'POST',
      data: payload,
    },
  );
  return data.card;
}

export async function apiUpdateBoardCard(
  boardId: string,
  rowId: string,
  payload: UpdateBoardCardPayload,
): Promise<BoardViewCard> {
  const data = await fetchDataApi<ApiSuccess<{ card: BoardViewCard }>>(
    `/boards/${encodeURIComponent(boardId)}/cards/${encodeURIComponent(rowId)}`,
    {
      method: 'PATCH',
      data: payload,
    },
  );
  return data.card;
}

export async function apiDeleteBoardCard(boardId: string, rowId: string): Promise<void> {
  await fetchDataApi(
    `/boards/${encodeURIComponent(boardId)}/cards/${encodeURIComponent(rowId)}`,
    { method: 'DELETE' },
  );
}

export async function apiMoveBoardCard(
  boardId: string,
  rowId: string,
  payload: MoveBoardCardPayload,
): Promise<MoveBoardCardResult> {
  const data = await fetchDataApi<ApiSuccess<MoveBoardCardResult>>(
    `/boards/${encodeURIComponent(boardId)}/cards/${encodeURIComponent(rowId)}/move`,
    {
      method: 'POST',
      data: payload,
    },
  );

  return {
    boardId: data.boardId,
    rowId: data.rowId,
    fromColumnKey: data.fromColumnKey ?? null,
    toColumnKey: data.toColumnKey,
    toIndex: data.toIndex,
  };
}

export async function apiAddBoardColumn(
  boardId: string,
  payload: AddBoardColumnPayload,
): Promise<{ board: BoardDetail; columnKey: string }> {
  const data = await fetchDataApi<ApiSuccess<{ board: BoardDetail; columnKey: string }>>(
    `/boards/${encodeURIComponent(boardId)}/columns`,
    {
      method: 'POST',
      data: payload,
    },
  );
  return {
    board: data.board,
    columnKey: data.columnKey,
  };
}

export async function apiRenameBoardColumn(
  boardId: string,
  columnKey: string,
  payload: RenameBoardColumnPayload,
): Promise<{ board: BoardDetail; columnKey: string; previousColumnKey: string }> {
  const data = await fetchDataApi<ApiSuccess<{
    board: BoardDetail;
    columnKey: string;
    previousColumnKey: string;
  }>>(
    `/boards/${encodeURIComponent(boardId)}/columns/${encodeURIComponent(columnKey)}`,
    {
      method: 'PATCH',
      data: payload,
    },
  );
  return {
    board: data.board,
    columnKey: data.columnKey,
    previousColumnKey: data.previousColumnKey,
  };
}

export async function apiRemoveBoardColumn(
  boardId: string,
  columnKey: string,
): Promise<{ board: BoardDetail; columnKey: string }> {
  const data = await fetchDataApi<ApiSuccess<{ board: BoardDetail; columnKey: string }>>(
    `/boards/${encodeURIComponent(boardId)}/columns/${encodeURIComponent(columnKey)}`,
    { method: 'DELETE' },
  );
  return {
    board: data.board,
    columnKey: data.columnKey,
  };
}

export async function apiUpdateBoardColumnsOrder(
  boardId: string,
  payload: UpdateBoardColumnsOrderPayload,
): Promise<BoardDetail> {
  const data = await fetchDataApi<ApiSuccess<{ board: BoardDetail }>>(
    `/boards/${encodeURIComponent(boardId)}/columns/order`,
    {
      method: 'PUT',
      data: payload,
    },
  );
  return data.board;
}

export async function apiUpdateBoardCardOrder(
  boardId: string,
  columnKey: string,
  payload: UpdateBoardCardOrderPayload,
): Promise<{ boardId: string; columnKey: string; rowIds: string[] }> {
  const data = await fetchDataApi<ApiSuccess<{
    boardId: string;
    columnKey: string;
    rowIds: string[];
  }>>(
    `/boards/${encodeURIComponent(boardId)}/columns/${encodeURIComponent(columnKey)}/order`,
    {
      method: 'PUT',
      data: payload,
    },
  );

  return {
    boardId: data.boardId,
    columnKey: data.columnKey,
    rowIds: data.rowIds ?? [],
  };
}
